import { useState, useEffect } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card, CardContent } from "../components/ui/card";
import { useCurrencyRates, toDKK, formatDKK } from "../hooks/useCurrencyRates";

// ─── Real billing accounts (organized by actual billing entity, not geo) ──
interface BillingAccount {
  id: string;
  provider: string;
  accountLabel: string;       // e.g. "post@sparkredit.se"
  geos: string[];             // which geos this covers
  // Auto-fetched from API (null = not available via API)
  apiFetched: boolean;
  plan: string | null;
  monthlyCost: number | null;
  currency: string;
  renewalDate: string | null;       // fixed date from API
  renewalDayOfMonth: number | null; // recurring monthly day (e.g. 6 = 6th)
  listSize: string | null;
  paymentPlan: string | null;
}

// Compute next renewal date from a day-of-month
function getNextRenewalDate(day: number): string {
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), day);
  const target = thisMonth > now ? thisMonth : new Date(now.getFullYear(), now.getMonth() + 1, day);
  return target.toISOString().split("T")[0];
}

// Pre-populated from actual API calls on 2026-04-03
const INITIAL_ACCOUNTS: BillingAccount[] = [
  {
    id: "gr_se_no",
    provider: "getresponse",
    accountLabel: "post@sparkredit.se",
    geos: ["SE", "NO"],
    apiFetched: true,
    plan: "Email Marketing",
    monthlyCost: 469.00,
    currency: "EUR",
    renewalDate: "2026-04-17",
    renewalDayOfMonth: null,
    listSize: "100,000",
    paymentPlan: "Monthly",
  },
  {
    id: "gr_de",
    provider: "getresponse",
    accountLabel: "post@gewinnspielen.com",
    geos: ["DE"],
    apiFetched: true,
    plan: "Email Marketing",
    monthlyCost: 259.00,
    currency: "EUR",
    renewalDate: "2026-04-26",
    renewalDayOfMonth: null,
    listSize: "50,000",
    paymentPlan: "Monthly",
  },
  {
    id: "gr_fi",
    provider: "getresponse",
    accountLabel: "posti@tarjouksesi.com",
    geos: ["FI"],
    apiFetched: true,
    plan: "Email Marketing",
    monthlyCost: 259.00,
    currency: "EUR",
    renewalDate: "2026-05-02",
    renewalDayOfMonth: null,
    listSize: "50,000",
    paymentPlan: "Monthly",
  },
  {
    id: "br_fi",
    provider: "brevo",
    accountLabel: "uutiskirje@lainatsuomessa.com",
    geos: ["FI"],
    apiFetched: true,
    plan: "Business",
    monthlyCost: null,        // Brevo API doesn't expose price
    currency: "EUR",
    renewalDate: "2026-04-27",
    renewalDayOfMonth: null,
    listSize: null,
    paymentPlan: "Paid",
  },
  {
    id: "br_sl",
    provider: "brevo",
    accountLabel: "info@email.simplyloan.se",
    geos: ["SE", "FI", "NO"],
    apiFetched: true,
    plan: "Business",
    monthlyCost: null,        // Brevo API doesn't expose price
    currency: "EUR",
    renewalDate: "2026-04-20",
    renewalDayOfMonth: null,
    listSize: null,
    paymentPlan: "Paid",
  },
  {
    id: "fb_no",
    provider: "feedblitz",
    accountLabel: "onlinelaan.com",
    geos: ["NO"],
    apiFetched: true,
    plan: "Core 150K",
    monthlyCost: 499.00,
    currency: "USD",
    renewalDate: null,
    renewalDayOfMonth: 6,
    listSize: "139,308",
    paymentPlan: "Monthly",
  },
  {
    id: "fb_fi",
    provider: "feedblitz",
    accountLabel: "tarjouksetpostitse.com",
    geos: ["FI"],
    apiFetched: true,
    plan: "Core 50K",
    monthlyCost: 299.00,
    currency: "USD",
    renewalDate: null,
    renewalDayOfMonth: 10,
    listSize: "47,050",
    paymentPlan: "Monthly",
  },
  {
    id: "fb_se",
    provider: "feedblitz",
    accountLabel: "onlinetilbud.com",
    geos: ["SE"],
    apiFetched: true,
    plan: "Core 275K",
    monthlyCost: 749.00,
    currency: "USD",
    renewalDate: null,
    renewalDayOfMonth: 11,
    listSize: "264,584",
    paymentPlan: "Monthly",
  },
  {
    id: "fb_de",
    provider: "feedblitz",
    accountLabel: "kreditoptionen.de",
    geos: ["DE"],
    apiFetched: true,
    plan: "Core 175K",
    monthlyCost: 549.00,
    currency: "USD",
    renewalDate: null,
    renewalDayOfMonth: 21,
    listSize: "174,918",
    paymentPlan: "Monthly",
  },
  {
    id: "ms_se",
    provider: "moosend",
    accountLabel: "Moosend",
    geos: ["SE"],
    apiFetched: false,
    plan: null,
    monthlyCost: null,
    currency: "EUR",
    renewalDate: null,
    renewalDayOfMonth: null,
    listSize: null,
    paymentPlan: null,
  },
  {
    id: "ml_fi",
    provider: "mailerlite",
    accountLabel: "posti@tarjouksesi.com (ML)",
    geos: ["FI"],
    apiFetched: true,
    plan: "Advanced",
    monthlyCost: 214.00,
    currency: "USD",
    renewalDate: null,
    renewalDayOfMonth: null,
    listSize: "25,000–30,000",
    paymentPlan: "Monthly",
  },
];

// ─── Manual overrides (persisted to localStorage) ──────────────────────────
interface ManualOverrides {
  [accountId: string]: {
    monthlyCost?: string;
    currency?: string;
    renewalDate?: string;
    plan?: string;
    notes?: string;
  };
}

const STORAGE_KEY = "afima_esp_billing_overrides";

// ─── Provider styling ──────────────────────────────────────────────────────
const providerColors: Record<string, string> = {
  getresponse: "bg-blue-100 text-blue-800",
  brevo: "bg-emerald-100 text-emerald-800",
  feedblitz: "bg-orange-100 text-orange-800",
  moosend: "bg-purple-100 text-purple-800",
  mailerlite: "bg-teal-100 text-teal-800",
};

const providerLogos: Record<string, string> = {
  getresponse: "📧",
  brevo: "💌",
  feedblitz: "📨",
  moosend: "📬",
  mailerlite: "✉️",
};

const countryFlags: Record<string, string> = {
  SE: "🇸🇪", FI: "🇫🇮", NO: "🇳🇴", DE: "🇩🇪",
};

// ─── Component ─────────────────────────────────────────────────────────────
export default function AccountingEspPage() {
  const [overrides, setOverrides] = useState<ManualOverrides>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saved, setSaved] = useState(false);
  const { rates, lastUpdated: ratesDate, isLoading: ratesLoading } = useCurrencyRates();

  // Load overrides from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setOverrides(JSON.parse(raw));
    } catch {
      console.warn("Failed to load billing overrides");
    }
  }, []);

  const getOverride = (id: string) => overrides[id] || {};

  const updateOverride = (id: string, field: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [id]: { ...getOverride(id), [field]: value },
    }));
    setHasChanges(true);
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    setHasChanges(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  // ─── Effective values (API data + manual overrides) ────────────────────
  const getEffectiveCost = (acc: BillingAccount): number | null => {
    const ov = getOverride(acc.id);
    if (ov.monthlyCost) return parseFloat(ov.monthlyCost);
    return acc.monthlyCost;
  };

  const getEffectiveRenewal = (acc: BillingAccount): string | null => {
    const ov = getOverride(acc.id);
    if (ov.renewalDate) return ov.renewalDate;
    if (acc.renewalDate) return acc.renewalDate;
    if (acc.renewalDayOfMonth) return getNextRenewalDate(acc.renewalDayOfMonth);
    return null;
  };

  const getEffectivePlan = (acc: BillingAccount): string => {
    const ov = getOverride(acc.id);
    return ov.plan || acc.plan || "";
  };

  // ─── Summary ───────────────────────────────────────────────────────────
  const currencySymbol: Record<string, string> = { EUR: "€", USD: "$" };

  const totalByCurrency: Record<string, number> = {};
  INITIAL_ACCOUNTS.forEach((acc) => {
    const cost = getEffectiveCost(acc);
    if (cost && cost > 0) {
      totalByCurrency[acc.currency] = (totalByCurrency[acc.currency] || 0) + cost;
    }
  });

  const accountsWithCost = INITIAL_ACCOUNTS.filter((a) => getEffectiveCost(a)).length;

  const upcomingRenewals = INITIAL_ACCOUNTS
    .map((a) => ({ label: a.accountLabel, date: getEffectiveRenewal(a) }))
    .filter((r) => r.date)
    .sort((a, b) => (a.date! > b.date! ? 1 : -1));

  const nextRenewalStr = upcomingRenewals.length > 0
    ? `${upcomingRenewals[0].date} (${upcomingRenewals[0].label.split("@")[0]})`
    : "—";

  // Cost by provider, grouped by currency
  const costByProviderCurrency: Record<string, Record<string, number>> = {};
  INITIAL_ACCOUNTS.forEach((acc) => {
    const cost = getEffectiveCost(acc);
    if (cost && cost > 0) {
      if (!costByProviderCurrency[acc.provider]) costByProviderCurrency[acc.provider] = {};
      costByProviderCurrency[acc.provider][acc.currency] = (costByProviderCurrency[acc.provider][acc.currency] || 0) + cost;
    }
  });

  // Total in DKK
  const totalDKK = INITIAL_ACCOUNTS.reduce((sum, acc) => {
    const cost = getEffectiveCost(acc);
    if (!cost || cost <= 0) return sum;
    const dkk = toDKK(cost, acc.currency, rates);
    return sum + (dkk || 0);
  }, 0);

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">💰 ESP Costs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Organized by billing account. Data auto-fetched where available, editable for the rest.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 font-medium animate-pulse">Unsaved changes</span>
            )}
            {saved && (
              <span className="text-sm text-green-600 font-medium">✓ Saved</span>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                hasChanges
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500 font-medium">Total Monthly Cost</p>
              <div className="mt-1 space-y-0.5">
                {Object.entries(totalByCurrency).map(([cur, total]) => (
                  <p key={cur} className="text-2xl font-bold text-gray-900">
                    {currencySymbol[cur] || cur}{total.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className="text-sm font-normal text-gray-400 ml-1">{cur}</span>
                  </p>
                ))}
                {Object.keys(totalByCurrency).length === 0 && (
                  <p className="text-2xl font-bold text-gray-900">—</p>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {accountsWithCost} of {INITIAL_ACCOUNTS.length} accounts have cost data
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500 font-medium">Next Renewal</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{nextRenewalStr}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500 font-medium">Total in DKK</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {ratesLoading ? (
                  <span className="text-gray-400 animate-pulse">Loading...</span>
                ) : (
                  formatDKK(totalDKK)
                )}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Rates: {ratesDate === "fallback" ? "fallback" : ratesDate}
                {rates.EUR && ` · 1€=${rates.EUR.toFixed(2)} · 1$=${rates.USD?.toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500 font-medium">Active Providers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {Array.from(new Set(INITIAL_ACCOUNTS.map((a) => a.provider))).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Cost by Provider */}
        {Object.keys(costByProviderCurrency).length > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Cost by Provider</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(costByProviderCurrency)
                  .sort((a, b) => {
                    const totalA = Object.values(a[1]).reduce((s, v) => s + v, 0);
                    const totalB = Object.values(b[1]).reduce((s, v) => s + v, 0);
                    return totalB - totalA;
                  })
                  .map(([provider, currencies]) => (
                    <div
                      key={provider}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        providerColors[provider] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      <span>{providerLogos[provider]}</span>
                      <span className="capitalize">{provider}</span>
                      <span className="font-bold">
                        {Object.entries(currencies).map(([cur, cost], i) => (
                          <span key={cur}>
                            {i > 0 && " + "}
                            {currencySymbol[cur] || cur}{cost.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Renewals Timeline */}
        {upcomingRenewals.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Upcoming Renewals</p>
              <div className="flex flex-wrap gap-4">
                {upcomingRenewals.map((r, i) => {
                  const daysUntil = Math.ceil(
                    (new Date(r.date!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  );
                  const urgency =
                    daysUntil <= 7 ? "bg-red-50 border-red-200 text-red-800" :
                    daysUntil <= 14 ? "bg-amber-50 border-amber-200 text-amber-800" :
                    "bg-gray-50 border-gray-200 text-gray-700";
                  return (
                    <div key={i} className={`border rounded-lg px-3 py-2 text-sm ${urgency}`}>
                      <span className="font-semibold">{r.date}</span>
                      <span className="ml-2 opacity-75">({daysUntil}d)</span>
                      <span className="block text-xs opacity-60">{r.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Provider</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Account</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Geos</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Plan</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">List Size</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Monthly Cost</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Next Renewal</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Source</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">DKK</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {INITIAL_ACCOUNTS.map((acc, idx) => {
                    const ov = getOverride(acc.id);
                    const effectiveCost = getEffectiveCost(acc);
                    const effectiveRenewal = getEffectiveRenewal(acc);
                    const effectivePlan = getEffectivePlan(acc);

                    return (
                      <tr
                        key={acc.id}
                        className={`border-b transition-colors ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        } hover:bg-indigo-50/30`}
                      >
                        {/* Provider */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                              providerColors[acc.provider] || "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {providerLogos[acc.provider]} {acc.provider}
                          </span>
                        </td>

                        {/* Account email */}
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{acc.accountLabel}</span>
                        </td>

                        {/* Geos */}
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {acc.geos.map((g) => (
                              <span key={g} className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs font-medium">
                                {countryFlags[g]} {g}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Plan */}
                        <td className="px-4 py-3">
                          {acc.apiFetched && acc.plan ? (
                            <span className="text-gray-900">{effectivePlan}</span>
                          ) : (
                            <input
                              type="text"
                              className="w-full bg-transparent border-b border-dashed border-gray-300 focus:border-indigo-400 focus:outline-none px-1 py-0.5 text-sm"
                              placeholder="Enter plan..."
                              value={ov.plan || ""}
                              onChange={(e) => updateOverride(acc.id, "plan", e.target.value)}
                            />
                          )}
                        </td>

                        {/* List Size */}
                        <td className="px-4 py-3 text-gray-600">
                          {acc.listSize || "—"}
                        </td>

                        {/* Monthly Cost */}
                        <td className="px-4 py-3 text-right">
                          {acc.apiFetched && acc.monthlyCost !== null ? (
                            <span className="font-bold text-gray-900">
                              {currencySymbol[acc.currency] || acc.currency}{acc.monthlyCost.toFixed(2)}
                              <span className="text-xs font-normal text-gray-400 ml-1">{acc.currency}</span>
                            </span>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-gray-400 text-xs">{currencySymbol[acc.currency] || acc.currency}</span>
                              <input
                                type="number"
                                className="w-20 bg-transparent border-b border-dashed border-gray-300 focus:border-indigo-400 focus:outline-none text-right px-1 py-0.5 text-sm font-bold"
                                placeholder="0.00"
                                value={ov.monthlyCost || ""}
                                onChange={(e) => updateOverride(acc.id, "monthlyCost", e.target.value)}
                              />
                            </div>
                          )}
                        </td>

                        {/* Next Renewal */}
                        <td className="px-4 py-3">
                          {acc.apiFetched && acc.renewalDate ? (
                            <span className="text-gray-900">{effectiveRenewal}</span>
                          ) : (
                            <input
                              type="date"
                              className="bg-transparent border-b border-dashed border-gray-300 focus:border-indigo-400 focus:outline-none px-1 py-0.5 text-sm"
                              value={ov.renewalDate || ""}
                              onChange={(e) => updateOverride(acc.id, "renewalDate", e.target.value)}
                            />
                          )}
                        </td>

                        {/* Source badge */}
                        <td className="px-4 py-3">
                          {acc.apiFetched ? (
                            <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full text-xs font-medium">
                              ✓ API
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-gray-50 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full text-xs font-medium">
                              ✏️ Manual
                            </span>
                          )}
                        </td>

                        {/* DKK equivalent */}
                        <td className="px-4 py-3 text-right">
                          {effectiveCost ? (
                            <span className="text-sm text-gray-600">
                              {(() => {
                                const dkk = toDKK(effectiveCost, acc.currency, rates);
                                return dkk ? formatDKK(dkk) : "—";
                              })()}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Notes */}
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            className="w-full bg-transparent border-b border-dashed border-gray-300 focus:border-indigo-400 focus:outline-none px-1 py-0.5 text-sm text-gray-600"
                            placeholder="—"
                            value={ov.notes || ""}
                            onChange={(e) => updateOverride(acc.id, "notes", e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            Data last fetched from APIs: 2026-04-03. Manual entries saved to browser localStorage.
          </span>
          <span>
            GetResponse via <code className="bg-gray-100 px-1 rounded">GET /v3/accounts/billing</code> •
            Brevo via <code className="bg-gray-100 px-1 rounded">GET /v3/account</code> (no price) •
            MailerLite via <code className="bg-gray-100 px-1 rounded">GET /api/account</code> •
            Moosend/Feedblitz: no billing API
          </span>
        </div>
      </div>
    </MainLayout>
  );
}
